import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Edit,
  Trash2,
  X,
  DollarSign,
  ShoppingCart,
  Package,
  Image as ImageIcon,
  Save,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Upload,
  GripVertical,
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';

const SIZES_PRESET = ['S', 'M', 'L', 'XL', 'XXL'];

const EventMerchTab = ({ eventId }) => {
  const [merchItems, setMerchItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [activeView, setActiveView] = useState('items'); // 'items' | 'orders'

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    variants: SIZES_PRESET.map(size => ({ size, stock: 0, sold: 0 })),
    images: [],
    sale_start: '',
    sale_end: '',
    is_active: true,
  });

  // Image upload state
  const [uploading, setUploading] = useState(false);

  // ── Load data ──

  const loadMerch = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('event_merchandise')
        .select('*')
        .eq('event_id', eventId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setMerchItems(data || []);
    } catch (err) {
      console.error('Error loading merch:', err);
    }
  }, [eventId]);

  const loadOrders = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('event_merch_orders')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (err) {
      console.error('Error loading merch orders:', err);
    }
  }, [eventId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadMerch(), loadOrders()]);
      setLoading(false);
    };
    init();
  }, [loadMerch, loadOrders]);

  // ── Form helpers ──

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      price: '',
      variants: SIZES_PRESET.map(size => ({ size, stock: 0, sold: 0 })),
      images: [],
      sale_start: '',
      sale_end: '',
      is_active: true,
    });
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || '',
      description: item.description || '',
      price: item.price?.toString() || '',
      variants: item.variants || SIZES_PRESET.map(size => ({ size, stock: 0, sold: 0 })),
      images: item.images || [],
      sale_start: item.sale_start ? new Date(item.sale_start).toISOString().slice(0, 16) : '',
      sale_end: item.sale_end ? new Date(item.sale_end).toISOString().slice(0, 16) : '',
      is_active: item.is_active !== false,
    });
    setShowModal(true);
  };

  // ── Image upload ──

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const result = await uploadToCloudinary(file, 'image', 'events/merchandise');
        if (result?.url) urls.push(result.url);
      }
      setForm(prev => ({ ...prev, images: [...prev.images, ...urls] }));
    } catch (err) {
      console.error('Image upload failed:', err);
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  // ── Variant helpers ──

  const updateVariantStock = (index, stock) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === index ? { ...v, stock: parseInt(stock) || 0 } : v
      ),
    }));
  };

  const addCustomVariant = () => {
    setForm(prev => ({
      ...prev,
      variants: [...prev.variants, { size: '', stock: 0, sold: 0 }],
    }));
  };

  const updateVariantName = (index, name) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === index ? { ...v, size: name } : v
      ),
    }));
  };

  const removeVariant = (index) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  // ── Save ──

  const handleSave = async () => {
    if (!form.title || !form.price) return;
    setSaving(true);

    try {
      const payload = {
        event_id: eventId,
        title: form.title,
        description: form.description || null,
        price: parseFloat(form.price),
        variants: form.variants.filter(v => v.size),
        images: form.images,
        sale_start: form.sale_start || null,
        sale_end: form.sale_end || null,
        is_active: form.is_active,
        updated_at: new Date().toISOString(),
      };

      if (editingItem) {
        const { error } = await supabase
          .from('event_merchandise')
          .update(payload)
          .eq('id', editingItem.id);
        if (error) throw error;
      } else {
        payload.sort_order = merchItems.length;
        const { error } = await supabase
          .from('event_merchandise')
          .insert(payload);
        if (error) throw error;
      }

      await loadMerch();
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error('Error saving merch:', err);
      alert('Failed to save merchandise item');
    }
    setSaving(false);
  };

  // ── Delete ──

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this merchandise item? This cannot be undone.')) return;
    setDeleting(id);
    try {
      const { error } = await supabase
        .from('event_merchandise')
        .delete()
        .eq('id', id);
      if (error) throw error;
      await loadMerch();
    } catch (err) {
      console.error('Error deleting merch:', err);
      alert('Failed to delete. It may have existing orders.');
    }
    setDeleting(null);
  };

  // ── Toggle active ──

  const toggleActive = async (item) => {
    try {
      const { error } = await supabase
        .from('event_merchandise')
        .update({ is_active: !item.is_active, updated_at: new Date().toISOString() })
        .eq('id', item.id);
      if (error) throw error;
      await loadMerch();
    } catch (err) {
      console.error('Error toggling merch:', err);
    }
  };

  // ── Update fulfillment ──

  const updateFulfillment = async (orderId, status) => {
    try {
      const { error } = await supabase
        .from('event_merch_orders')
        .update({ fulfillment_status: status, updated_at: new Date().toISOString() })
        .eq('id', orderId);
      if (error) throw error;
      await loadOrders();
    } catch (err) {
      console.error('Error updating fulfillment:', err);
    }
  };

  // ── Computed stats ──

  const totalStock = merchItems.reduce((sum, item) => {
    return sum + (item.variants || []).reduce((s, v) => s + (v.stock || 0), 0);
  }, 0);

  const totalSold = orders
    .filter(o => o.payment_status === 'paid')
    .reduce((sum, o) => sum + (o.quantity || 1), 0);

  const totalRevenue = orders
    .filter(o => o.payment_status === 'paid')
    .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);

  const paidOrders = orders.filter(o => o.payment_status === 'paid');

  // ── Render ──

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        Loading merchandise...
      </div>
    );
  }

  return (
    <div className="merch-tab">
      {/* Stats bar */}
      <div className="ticket-stats-grid" style={{ marginBottom: '16px' }}>
        <div className="ticket-stat-card revenue">
          <div className="ticket-stat-icon"><DollarSign /></div>
          <div className="ticket-stat-content">
            <div className="ticket-stat-value">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalRevenue)}
            </div>
            <div className="ticket-stat-label">Merch Revenue</div>
          </div>
        </div>
        <div className="ticket-stat-card sold">
          <div className="ticket-stat-icon"><Package /></div>
          <div className="ticket-stat-content">
            <div className="ticket-stat-value">
              {totalSold} <span className="ticket-stat-total">/ {totalStock}</span>
            </div>
            <div className="ticket-stat-label">Items Sold</div>
          </div>
        </div>
        <div className="ticket-stat-card checkins">
          <div className="ticket-stat-icon"><ShoppingCart /></div>
          <div className="ticket-stat-content">
            <div className="ticket-stat-value">{paidOrders.length}</div>
            <div className="ticket-stat-label">Orders</div>
          </div>
        </div>
      </div>

      {/* Sub-tabs: Items / Orders */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          className={`btn-secondary ${activeView === 'items' ? 'active' : ''}`}
          onClick={() => setActiveView('items')}
          style={activeView === 'items' ? { background: 'rgba(139, 92, 246, 0.2)', borderColor: '#8b5cf6', color: '#c4b5fd' } : {}}
        >
          <Package size={16} /> Items ({merchItems.length})
        </button>
        <button
          className={`btn-secondary ${activeView === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveView('orders')}
          style={activeView === 'orders' ? { background: 'rgba(139, 92, 246, 0.2)', borderColor: '#8b5cf6', color: '#c4b5fd' } : {}}
        >
          <ShoppingCart size={16} /> Orders ({paidOrders.length})
        </button>
      </div>

      {/* ── ITEMS VIEW ── */}
      {activeView === 'items' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#e2e8f0' }}>Merchandise Items</h3>
            <button className="btn-primary" onClick={openCreateModal}>
              <Plus size={16} /> Add Item
            </button>
          </div>

          {merchItems.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.1)' }}>
              <Package size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
              <p>No merchandise items yet</p>
              <button className="btn-primary" onClick={openCreateModal} style={{ marginTop: '12px' }}>
                <Plus size={16} /> Add Your First Item
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {merchItems.map((item) => {
                const itemSold = (item.variants || []).reduce((s, v) => s + (v.sold || 0), 0);
                const itemStock = (item.variants || []).reduce((s, v) => s + (v.stock || 0), 0);
                const firstImage = (item.images || [])[0];
                // Compute actual order counts by size from orders data
                const itemOrders = orders.filter(o => o.merch_id === item.id && o.payment_status === 'paid');
                const ordersBySize = {};
                let itemOrderQty = 0;
                let itemOrderRevenue = 0;
                itemOrders.forEach(o => {
                  const sz = o.size || 'N/A';
                  ordersBySize[sz] = (ordersBySize[sz] || 0) + (o.quantity || 1);
                  itemOrderQty += (o.quantity || 1);
                  itemOrderRevenue += parseFloat(o.total_amount || 0);
                });

                return (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      gap: '16px',
                      padding: '16px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.08)',
                      alignItems: 'center',
                      opacity: item.is_active ? 1 : 0.5,
                    }}
                  >
                    {/* Thumbnail */}
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden',
                      background: 'rgba(255,255,255,0.05)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {firstImage ? (
                        <img src={firstImage} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <ImageIcon size={24} style={{ color: '#475569' }} />
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h4 style={{ margin: 0, color: '#e2e8f0', fontSize: '15px' }}>{item.title}</h4>
                        {!item.is_active && (
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>
                            Inactive
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px' }}>
                        {item.description?.substring(0, 80)}{item.description?.length > 80 ? '...' : ''}
                      </div>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '13px' }}>
                        <span style={{ color: '#8b5cf6', fontWeight: 600 }}>
                          ${parseFloat(item.price).toFixed(2)}
                        </span>
                        <span style={{ color: '#94a3b8' }}>
                          {itemOrderQty} sold / {itemStock} stock
                        </span>
                        <span style={{ color: '#10b981', fontWeight: 600 }}>
                          ${itemOrderRevenue.toFixed(2)} revenue
                        </span>
                      </div>
                      {/* Size breakdown from actual orders */}
                      {itemOrderQty > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                          {(item.variants || []).map(v => v.size).filter(Boolean).map(size => {
                            const qty = ordersBySize[size] || 0;
                            const stockForSize = (item.variants || []).find(v => v.size === size)?.stock || 0;
                            return (
                              <span
                                key={size}
                                style={{
                                  fontSize: '11px',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  background: qty > 0 ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.04)',
                                  color: qty > 0 ? '#c4b5fd' : '#64748b',
                                  border: `1px solid ${qty > 0 ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255,255,255,0.06)'}`,
                                }}
                              >
                                {size}: {qty}/{stockForSize}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button
                        onClick={() => toggleActive(item)}
                        className="btn-secondary"
                        title={item.is_active ? 'Deactivate' : 'Activate'}
                        style={{ padding: '8px' }}
                      >
                        {item.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        onClick={() => openEditModal(item)}
                        className="btn-secondary"
                        style={{ padding: '8px' }}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="deactivate-btn"
                        disabled={deleting === item.id}
                        style={{ padding: '8px' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── ORDERS VIEW ── */}
      {activeView === 'orders' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: 0, color: '#e2e8f0' }}>Merch Orders</h3>
          </div>

          {paidOrders.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
              <ShoppingCart size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
              <p>No orders yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {paidOrders.map((order) => {
                const merchItem = merchItems.find(m => m.id === order.merch_id);
                return (
                  <div
                    key={order.id}
                    style={{
                      padding: '14px 16px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '10px',
                      border: '1px solid rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 500 }}>
                        {order.first_name} {order.last_name}
                        <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: '8px' }}>{order.email}</span>
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px', display: 'flex', gap: '12px' }}>
                        <span>{merchItem?.title || 'Unknown Item'}</span>
                        <span>Size: <strong style={{ color: '#c4b5fd' }}>{order.size}</strong></span>
                        <span>Qty: <strong>{order.quantity}</strong></span>
                        <span>${parseFloat(order.total_amount).toFixed(2)}</span>
                        <span>{order.pickup_or_ship === 'ship' ? '📦 Ship' : '🏪 Pickup'}</span>
                      </div>
                    </div>

                    {/* Fulfillment status */}
                    <select
                      value={order.fulfillment_status || 'pending'}
                      onChange={(e) => updateFulfillment(order.id, e.target.value)}
                      style={{
                        background: order.fulfillment_status === 'fulfilled'
                          ? 'rgba(16,185,129,0.15)'
                          : order.fulfillment_status === 'shipped'
                            ? 'rgba(59,130,246,0.15)'
                            : 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '6px',
                        color: '#e2e8f0',
                        padding: '6px 10px',
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="fulfilled">Fulfilled</option>
                      <option value="shipped">Shipped</option>
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── CREATE / EDIT MODAL ── */}
      {showModal && createPortal(
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content ticket-type-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Merchandise' : 'Add Merchandise'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="close-btn"><X /></button>
            </div>

            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Title */}
              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Art Speaks T-Shirt"
                />
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Product description, materials, care instructions..."
                  rows={3}
                />
              </div>

              {/* Price */}
              <div className="form-group">
                <label>Price *</label>
                <div className="input-with-icon">
                  <DollarSign />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={e => setForm({ ...form, price: e.target.value })}
                    placeholder="30.00"
                  />
                </div>
              </div>

              {/* Images */}
              <div className="form-group">
                <label>Images</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {form.images.map((url, i) => (
                    <div key={i} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden' }}>
                      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        onClick={() => removeImage(i)}
                        style={{
                          position: 'absolute', top: '2px', right: '2px',
                          background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%',
                          width: '20px', height: '20px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', padding: 0,
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <label style={{
                    width: '80px', height: '80px', borderRadius: '8px',
                    border: '2px dashed rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: uploading ? 'wait' : 'pointer',
                    color: '#94a3b8',
                  }}>
                    {uploading ? '...' : <Upload size={20} />}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              {/* Size / Variant Inventory */}
              <div className="form-section">
                <h4>Size Inventory</h4>
                {form.variants.map((variant, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={variant.size}
                      onChange={e => updateVariantName(i, e.target.value)}
                      placeholder="Size name"
                      style={{ width: '100px' }}
                    />
                    <div className="input-with-icon" style={{ flex: 1 }}>
                      <Package size={14} />
                      <input
                        type="number"
                        min="0"
                        value={variant.stock}
                        onChange={e => updateVariantStock(i, e.target.value)}
                        placeholder="Stock"
                      />
                    </div>
                    {editingItem && (
                      <span style={{ color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {variant.sold || 0} sold
                      </span>
                    )}
                    <button
                      onClick={() => removeVariant(i)}
                      className="deactivate-btn"
                      style={{ padding: '6px' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button onClick={addCustomVariant} className="btn-secondary" style={{ fontSize: '13px' }}>
                  <Plus size={14} /> Add Size/Variant
                </button>
              </div>

              {/* Sale period */}
              <div className="form-section">
                <h4>Sale Period (Optional)</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Sale Start</label>
                    <input
                      type="datetime-local"
                      value={form.sale_start}
                      onChange={e => setForm({ ...form, sale_start: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Sale End</label>
                    <input
                      type="datetime-local"
                      value={form.sale_end}
                      onChange={e => setForm({ ...form, sale_end: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Active toggle */}
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  />
                  <span>Active (visible and available for purchase)</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary"
                disabled={saving || !form.title || !form.price}
              >
                {saving ? 'Saving...' : editingItem ? 'Update Item' : 'Create Item'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EventMerchTab;
